import React, { useEffect, useState } from 'react';
import DeleteConfirmModal from './DeleteConfirmModal';
import ColorWheelPicker from './ColorWheelPicker';
import '../pages/ManageSMRTCOP.css';

// Same colour options offered on the Subscriptions customization page.
const BANNER_PRESETS = ['#218838', '#4B0000', '#2F5597'];
const DEFAULT_BANNER_COLOR = '#2F5597';

interface AdminOrg {
  orgID: string;
  name: string;
  label: string;
  purpose: string;
  description: string;
  bannerName: string;
  bannerColor: string;
  createdAt: string;
  lastAccessedAt: string;
  lastAccessedBy: string;
}

interface AdminOrgMember {
  userID: string;
  email: string;
  name: string;
}

const API = process.env.REACT_APP_BACKEND_URL;

const formatDate = (s?: string) =>
  s ? new Date(s).toLocaleDateString() : '—';
const formatDateTime = (s?: string) =>
  s ? new Date(s).toLocaleString() : '—';

// SuperAdmin tab: stand up / manage lean internal "admin orgs" (CGAdmin, future
// per-project consoles) without touching Auth0 by hand.
export default function AdminOrgsPanel() {
  const [orgs, setOrgs] = useState<AdminOrg[]>([]);
  const [loading, setLoading] = useState(false);

  // Create modal
  const [createOpen, setCreateOpen] = useState(false);
  const [label, setLabel] = useState('');
  const [purpose, setPurpose] = useState('');
  const [description, setDescription] = useState('');
  const [bannerName, setBannerName] = useState('');
  const [bannerColor, setBannerColor] = useState(DEFAULT_BANNER_COLOR);
  const [emails, setEmails] = useState('');
  const [creating, setCreating] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // View (expand members inline)
  const [expandedID, setExpandedID] = useState<string | null>(null);
  const [members, setMembers] = useState<AdminOrgMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);

  // Edit modal
  const [editOrg, setEditOrg] = useState<AdminOrg | null>(null);
  const [editDesc, setEditDesc] = useState('');
  const [editLabel, setEditLabel] = useState('');
  const [editBannerName, setEditBannerName] = useState('');
  const [editBannerColor, setEditBannerColor] = useState(DEFAULT_BANNER_COLOR);
  const [editMembers, setEditMembers] = useState<AdminOrgMember[]>([]);
  const [addEmail, setAddEmail] = useState('');
  const [editBusy, setEditBusy] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Delete
  const [pendingDelete, setPendingDelete] = useState<AdminOrg | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/admin-orgs`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to load admin orgs');
      const data = await res.json();
      setOrgs(Array.isArray(data.adminOrgs) ? data.adminOrgs : []);
    } catch {
      setOrgs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const fetchMembers = async (orgID: string): Promise<AdminOrgMember[]> => {
    const res = await fetch(
      `${API}/admin/admin-orgs/${encodeURIComponent(orgID)}/members`,
      { credentials: 'include' },
    );
    if (!res.ok) throw new Error('Failed to load members');
    const data = await res.json();
    return Array.isArray(data.members) ? data.members : [];
  };

  const handleCreate = async () => {
    setError(null);
    setMessage(null);
    const cleanLabel = label.trim();
    if (!cleanLabel) {
      setError('Give the admin org a name.');
      return;
    }
    const memberEmails = emails
      .split(/[\s,]+/)
      .map((e) => e.trim())
      .filter(Boolean);
    setCreating(true);
    try {
      const res = await fetch(`${API}/admin/admin-orgs`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          label: cleanLabel,
          purpose: purpose.trim(),
          description: description.trim(),
          bannerName: bannerName.trim(),
          bannerColor: bannerColor.trim(),
          memberEmails,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to create admin org');
      const added = Array.isArray(data.added) ? data.added : [];
      const notFound = Array.isArray(data.notFound) ? data.notFound : [];
      setMessage(
        `Created "${cleanLabel}". Added: ${
          added.length ? added.join(', ') : 'none'
        }.${
          notFound.length ? ` Not found in Auth0: ${notFound.join(', ')}.` : ''
        }`,
      );
      setLabel('');
      setPurpose('');
      setDescription('');
      setBannerName('');
      setBannerColor(DEFAULT_BANNER_COLOR);
      setEmails('');
      setCreateOpen(false);
      load();
    } catch (err) {
      setError((err as Error).message || 'Failed to create admin org');
    } finally {
      setCreating(false);
    }
  };

  const openCreate = () => {
    setLabel('');
    setPurpose('');
    setDescription('');
    setBannerName('');
    setBannerColor(DEFAULT_BANNER_COLOR);
    setEmails('');
    setError(null);
    setMessage(null);
    setCreateOpen(true);
  };

  const toggleView = async (org: AdminOrg) => {
    if (expandedID === org.orgID) {
      setExpandedID(null);
      return;
    }
    setExpandedID(org.orgID);
    setMembers([]);
    setMembersLoading(true);
    try {
      setMembers(await fetchMembers(org.orgID));
    } catch {
      setMembers([]);
    } finally {
      setMembersLoading(false);
    }
  };

  const openEdit = async (org: AdminOrg) => {
    setEditOrg(org);
    setEditLabel(org.label || '');
    setEditDesc(org.description || '');
    setEditBannerName(org.bannerName || '');
    setEditBannerColor(org.bannerColor || DEFAULT_BANNER_COLOR);
    setEditError(null);
    setAddEmail('');
    setEditMembers([]);
    try {
      setEditMembers(await fetchMembers(org.orgID));
    } catch {
      setEditMembers([]);
    }
  };

  const saveDescription = async (): Promise<boolean> => {
    if (!editOrg) return false;
    if (!editLabel.trim()) {
      setEditError('Name is required');
      return false;
    }
    setEditBusy(true);
    setEditError(null);
    try {
      const res = await fetch(
        `${API}/admin/admin-orgs/${encodeURIComponent(editOrg.orgID)}`,
        {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            label: editLabel.trim(),
            description: editDesc.trim(),
            bannerName: editBannerName.trim(),
            bannerColor: editBannerColor.trim(),
          }),
        },
      );
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d?.error || 'Failed to save changes');
      }
      setOrgs((prev) =>
        prev.map((o) =>
          o.orgID === editOrg.orgID
            ? {
                ...o,
                label: editLabel.trim(),
                description: editDesc.trim(),
                bannerName: editBannerName.trim(),
                bannerColor: editBannerColor.trim(),
              }
            : o,
        ),
      );
      return true;
    } catch (err) {
      setEditError((err as Error).message);
      return false;
    } finally {
      setEditBusy(false);
    }
  };

  const handleEditSave = async () => {
    const ok = await saveDescription();
    if (ok) {
      setEditOrg(null);
      load();
    }
  };

  const addMember = async () => {
    if (!editOrg) return;
    const email = addEmail.trim();
    if (!email) return;
    setEditBusy(true);
    setEditError(null);
    try {
      const res = await fetch(
        `${API}/admin/admin-orgs/${encodeURIComponent(editOrg.orgID)}/members`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email }),
        },
      );
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d?.error || 'Failed to add member');
      setAddEmail('');
      setEditMembers(await fetchMembers(editOrg.orgID));
    } catch (err) {
      setEditError((err as Error).message);
    } finally {
      setEditBusy(false);
    }
  };

  const removeMember = async (userID: string) => {
    if (!editOrg) return;
    setEditBusy(true);
    setEditError(null);
    try {
      const res = await fetch(
        `${API}/admin/admin-orgs/${encodeURIComponent(
          editOrg.orgID,
        )}/members/${encodeURIComponent(userID)}`,
        { method: 'DELETE', credentials: 'include' },
      );
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d?.error || 'Failed to remove member');
      }
      setEditMembers((prev) => prev.filter((m) => m.userID !== userID));
    } catch (err) {
      setEditError((err as Error).message);
    } finally {
      setEditBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      const res = await fetch(
        `${API}/admin/admin-orgs/${encodeURIComponent(pendingDelete.orgID)}`,
        { method: 'DELETE', credentials: 'include' },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to delete admin org');
      setOrgs((prev) => prev.filter((o) => o.orgID !== pendingDelete.orgID));
      setPendingDelete(null);
    } catch (err) {
      setDeleteError((err as Error).message || 'Failed to delete admin org');
    } finally {
      setDeleting(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    boxSizing: 'border-box',
    background: '#2a2a2a',
    color: '#fff',
    border: '1px solid #444',
    borderRadius: 6,
    padding: '8px 10px',
    fontSize: '0.9rem',
  };
  const labelStyle: React.CSSProperties = {
    display: 'block',
    color: '#9aa0a6',
    fontSize: '0.78rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    marginBottom: 4,
  };
  const metaLabel: React.CSSProperties = {
    color: '#6b7280',
    fontSize: '0.68rem',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  };
  // Name · Purpose · Description · Created · Last Accessed · Actions
  const GRID = '1.2fr 1fr 1.6fr 0.9fr 1.3fr 120px';

  return (
    <div style={{ background: '#1e1e1e', borderRadius: 8, padding: 16 }}>
      {/* Header + Add Org */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 12,
        }}
      >
        <h2 style={{ margin: 0, fontSize: '1rem', color: '#e5e7eb' }}>
          Admin Orgs
        </h2>
        <button className="smrtcop-btn-primary" onClick={openCreate}>
          + Add Org
        </button>
      </div>

      {message && (
        <div
          style={{
            color: '#4ade80',
            fontSize: '0.85rem',
            marginBottom: 10,
          }}
        >
          {message}
        </div>
      )}

      {/* Table header — same style as the Organizations tab, frozen below tabs */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: GRID,
          gap: 12,
          padding: '8px 12px',
          background: '#1a1a1a',
          borderRadius: 6,
          fontSize: '0.8rem',
          fontWeight: 600,
          color: '#9aa0a6',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          marginBottom: 4,
          position: 'sticky',
          top: 0,
          zIndex: 5,
          boxShadow: '0 2px 6px rgba(0,0,0,0.5)',
        }}
      >
        <span>Name</span>
        <span>Purpose</span>
        <span>Description</span>
        <span>Created</span>
        <span>Last Accessed</span>
        <span style={{ textAlign: 'center' }}>Actions</span>
      </div>

      {loading ? (
        <div
          style={{
            padding: '20px 12px',
            color: '#9aa0a6',
            fontSize: '0.9rem',
            textAlign: 'center',
          }}
        >
          Loading…
        </div>
      ) : orgs.length === 0 ? (
        <div
          style={{
            padding: '20px 12px',
            color: '#9aa0a6',
            fontSize: '0.9rem',
            textAlign: 'center',
          }}
        >
          No admin orgs yet.
        </div>
      ) : (
        orgs.map((o) => (
          <React.Fragment key={o.orgID}>
            <div
              className="smrtcop-overview-row"
              style={{ gridTemplateColumns: GRID, fontSize: '0.9rem' }}
            >
              <span style={{ fontWeight: 600 }} title={o.orgID}>
                {o.label}
              </span>
              <span style={{ color: '#cbd5e1' }}>{o.purpose || '—'}</span>
              <span style={{ color: '#cbd5e1' }}>{o.description || '—'}</span>
              <span style={{ color: '#cbd5e1' }}>{formatDate(o.createdAt)}</span>
              <span
                style={{ color: '#cbd5e1' }}
                title={o.lastAccessedBy || ''}
              >
                {formatDateTime(o.lastAccessedAt)}
                {o.lastAccessedBy && (
                  <div
                    style={{
                      fontSize: '0.75rem',
                      color: '#9aa0a6',
                      marginTop: 2,
                    }}
                  >
                    {o.lastAccessedBy}
                  </div>
                )}
              </span>
              <div className="smrtcop-actions">
                <button
                  className="smrtcop-btn-icon smrtcop-btn-view"
                  title="View members"
                  aria-label={`View members of ${o.label}`}
                  onClick={() => toggleView(o)}
                >
                  👁
                </button>
                <button
                  className="smrtcop-btn-icon smrtcop-btn-edit"
                  title="Edit"
                  aria-label={`Edit ${o.label}`}
                  onClick={() => openEdit(o)}
                >
                  ✎
                </button>
                <button
                  className="smrtcop-btn-icon smrtcop-btn-delete"
                  title="Delete"
                  aria-label={`Delete ${o.label}`}
                  onClick={() => {
                    setDeleteError(null);
                    setPendingDelete(o);
                  }}
                >
                  🗑
                </button>
              </div>
            </div>

            {/* View expansion — members list, full-width under the row */}
            {expandedID === o.orgID && (
              <div
                style={{
                  background: '#252525',
                  border: '1px solid #333',
                  borderRadius: 6,
                  padding: '10px 12px',
                  margin: '2px 0 6px',
                }}
              >
                <div style={{ ...metaLabel, marginBottom: 6 }}>Members</div>
                {membersLoading ? (
                  <div style={{ color: '#9aa0a6', fontSize: '0.85rem' }}>
                    Loading…
                  </div>
                ) : members.length === 0 ? (
                  <div style={{ color: '#9aa0a6', fontSize: '0.85rem' }}>
                    No members.
                  </div>
                ) : (
                  <div style={{ display: 'grid', gap: 4 }}>
                    {members.map((m) => (
                      <div
                        key={m.userID}
                        style={{ color: '#cbd5e1', fontSize: '0.85rem' }}
                      >
                        {m.name ? `${m.name} · ` : ''}
                        {m.email || m.userID}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </React.Fragment>
        ))
      )}

      {/* Create modal */}
      {createOpen && (
        <div className="smrtdocs-modal-overlay">
          <div className="smrtdocs-modal" style={{ width: 520 }}>
            <div className="smrtdocs-modal-header">
              <h2 style={{ margin: 0, fontSize: '1rem' }}>Add admin org</h2>
            </div>
            <div className="smrtdocs-modal-body">
              <div style={{ display: 'grid', gap: 12 }}>
                <div>
                  <label style={labelStyle}>Admin org name</label>
                  <input
                    style={inputStyle}
                    type="text"
                    value={label}
                    autoFocus
                    placeholder="e.g. CGAdmin"
                    onChange={(e) => setLabel(e.target.value)}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Purpose (optional key)</label>
                  <input
                    style={inputStyle}
                    type="text"
                    value={purpose}
                    placeholder="e.g. caddiegames-courses"
                    onChange={(e) => setPurpose(e.target.value)}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Description (optional)</label>
                  <input
                    style={inputStyle}
                    type="text"
                    value={description}
                    placeholder="What this admin org manages"
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Banner name</label>
                  <input
                    style={inputStyle}
                    type="text"
                    value={bannerName}
                    placeholder="Header title, e.g. CGAdmin"
                    onChange={(e) => setBannerName(e.target.value)}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Banner colour</label>
                  <ColorWheelPicker
                    value={bannerColor}
                    ariaLabel="Banner colour"
                    presets={BANNER_PRESETS}
                    onChange={(hex) => setBannerColor(hex)}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Member emails</label>
                  <textarea
                    style={{ ...inputStyle, minHeight: 64, resize: 'vertical' }}
                    value={emails}
                    placeholder="one per line or comma-separated"
                    onChange={(e) => setEmails(e.target.value)}
                  />
                </div>
                {error && (
                  <div style={{ color: '#f87171', fontSize: '0.85rem' }}>
                    {error}
                  </div>
                )}
                <div
                  style={{ display: 'flex', justifyContent: 'space-between' }}
                >
                  <button
                    className="btn-cancel"
                    onClick={() => setCreateOpen(false)}
                    disabled={creating}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn-save"
                    onClick={handleCreate}
                    disabled={creating}
                  >
                    {creating ? 'Creating…' : 'Create'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {editOrg && (
        <div className="smrtdocs-modal-overlay">
          <div className="smrtdocs-modal" style={{ width: 520 }}>
            <div className="smrtdocs-modal-header">
              <h2 style={{ margin: 0, fontSize: '1rem' }}>
                Edit {editOrg.label}
              </h2>
            </div>
            <div className="smrtdocs-modal-body">
              <label style={labelStyle}>Name</label>
              <div style={{ marginBottom: 16 }}>
                <input
                  style={inputStyle}
                  type="text"
                  value={editLabel}
                  onChange={(e) => setEditLabel(e.target.value)}
                  placeholder="e.g. CGAdmin"
                />
              </div>

              <label style={labelStyle}>Description</label>
              <div style={{ marginBottom: 16 }}>
                <input
                  style={inputStyle}
                  type="text"
                  value={editDesc}
                  onChange={(e) => setEditDesc(e.target.value)}
                  placeholder="What this admin org manages"
                />
              </div>

              <label style={labelStyle}>Banner name</label>
              <div style={{ marginBottom: 16 }}>
                <input
                  style={inputStyle}
                  type="text"
                  value={editBannerName}
                  onChange={(e) => setEditBannerName(e.target.value)}
                  placeholder="Header title, e.g. CGAdmin"
                />
              </div>

              <label style={labelStyle}>Banner colour</label>
              <div style={{ marginBottom: 16 }}>
                <ColorWheelPicker
                  value={editBannerColor}
                  ariaLabel="Banner colour"
                  presets={BANNER_PRESETS}
                  onChange={(hex) => setEditBannerColor(hex)}
                />
              </div>

              <label style={labelStyle}>Members</label>
              <div style={{ display: 'grid', gap: 4, marginBottom: 10 }}>
                {editMembers.length === 0 ? (
                  <div style={{ color: '#9aa0a6', fontSize: '0.85rem' }}>
                    No members.
                  </div>
                ) : (
                  editMembers.map((m) => (
                    <div
                      key={m.userID}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 8,
                        background: '#2a2a2a',
                        border: '1px solid #3a3a3a',
                        borderRadius: 6,
                        padding: '6px 10px',
                      }}
                    >
                      <span
                        style={{
                          color: '#cbd5e1',
                          fontSize: '0.85rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {m.name ? `${m.name} · ` : ''}
                        {m.email || m.userID}
                      </span>
                      <button
                        className="smrtcop-btn-icon smrtcop-btn-delete"
                        title="Remove member"
                        aria-label={`Remove ${m.email || m.userID}`}
                        disabled={editBusy}
                        onClick={() => removeMember(m.userID)}
                      >
                        ✕
                      </button>
                    </div>
                  ))
                )}
              </div>

              <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                <input
                  style={inputStyle}
                  type="email"
                  value={addEmail}
                  placeholder="add member by email"
                  onChange={(e) => setAddEmail(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') addMember();
                  }}
                />
                <button
                  className="smrtcop-btn-primary"
                  onClick={addMember}
                  disabled={editBusy || !addEmail.trim()}
                  aria-label="Add member"
                  style={{ whiteSpace: 'nowrap' }}
                >
                  +
                </button>
              </div>

              {editError && (
                <div
                  style={{
                    color: '#ef9a9a',
                    fontSize: '0.85rem',
                    marginBottom: 12,
                  }}
                >
                  {editError}
                </div>
              )}

              <div
                style={{ display: 'flex', justifyContent: 'space-between' }}
              >
                <button
                  className="btn-cancel"
                  onClick={() => {
                    setEditOrg(null);
                    load();
                  }}
                  disabled={editBusy}
                >
                  Cancel
                </button>
                <button
                  className="btn-save"
                  onClick={handleEditSave}
                  disabled={editBusy}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <DeleteConfirmModal
        isOpen={!!pendingDelete}
        onClose={() => !deleting && setPendingDelete(null)}
        onConfirm={handleDelete}
        title="Delete admin org"
        message={
          pendingDelete
            ? `Permanently delete "${pendingDelete.label}"? This removes the Auth0 organization and every member's access to it. This cannot be undone.`
            : ''
        }
        confirmLabel={deleting ? 'Deleting…' : 'Delete'}
        isLoading={deleting}
        error={deleteError}
      />
    </div>
  );
}
